// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useCallback, useContext } from "react";
import * as R from "ramda";

import {
  Filter as StyledFilter,
  ListItem,
  SubHeading,
  Chatroom as StyledChatroom,
  Separator,
} from "./styles";
import { FilterWorkerContext } from "src/contexts/FilterWorkerContext";
import { filterEmbeddedFields } from "src/actions/workflows";
import Checkbox from "src/components/Checkbox";
import AllRecordsFilter from "./AllRecordsFilter";
import { getUniqueValues, getWorkerData } from "src/reducers";
import BlanksNonBlanks from "./BlanksNonBlanks";
import { getAllRecordsLabel } from "src/utils/filters";

type Props = {
  column: string,
  parentRef: any,
  handleClose: Function,
  setFilter: Function,
  toggleSortBy: Function,
  filter: Array<any>,
  isSortedDesc: boolean,
};

const Select = ({
  parentRef,
  column,
  handleClose,
  setFilter,
  toggleSortBy,
  filter,
}: Props) => {
  const dispatch = useDispatch();
  const filterWorker = useContext(FilterWorkerContext);
  const values = useSelector(({ app }) => getUniqueValues(app, column));
  const {
    workflow,
    fieldsById,
    instancesById,
    filters,
    chatroomAttributes,
    updatedInstances,
    allRecords,
  } = useSelector(({ app }) => getWorkerData(app));

  const allRecordsColumnId = column.includes("-")
    ? column.split("-")[0]
    : column;

  let workerData = {
    workflow,
    fieldsById: fieldsById.toJS(),
    instancesById,
    filters,
    chatroomAttributes,
    updatedInstances,
    allRecords,
  };

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const closeModal =
    ((event: any) => {
      if (event.keyCode === 13) {
        handleClose();
      }
    },
    [handleClose]);

  const clickOutside = useCallback(
    (event: any) => {
      if (parentRef && parentRef.current) {
        if (!parentRef.current.contains(event.target)) {
          handleClose();
        }
      }
    },
    [parentRef, handleClose],
  );

  useEffect(() => {
    // $FlowFixMe
    document.addEventListener("keydown", closeModal, false);
    document.addEventListener("click", clickOutside, false);
    return () => {
      // $FlowFixMe
      document.removeEventListener("keydown", closeModal, false);
      document.removeEventListener("click", clickOutside, false);
    };
  }, []);

  const handleSort = (ascending: boolean) => {
    if (ascending) {
      toggleSortBy(false, true);
    } else {
      toggleSortBy(true, true);
    }
    handleClose();
  };

  const handleSelect = useCallback(
    (event: Event, item: string | number) => {
      event.preventDefault();
      event.stopPropagation();

      if (R.includes(item, filter || [])) {
        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: R.reject(R.equals(item), filter),
          },
        };

        if (filterWorker) {
          filterWorker.postMessage(workerData, (result) => {
            dispatch(filterEmbeddedFields({ instances: result }));
          });
        }
        setFilter(column, R.reject(R.equals(item), filter));
      } else {
        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: [...(filter || []), item],
          },
        };

        if (filterWorker) {
          filterWorker.postMessage(workerData, (result) => {
            dispatch(filterEmbeddedFields({ instances: result }));
          });
        }
        setFilter(column, [...(filter || []), item]);
      }
    },
    [column, filter, allRecords],
  );

  return (
    <StyledFilter onClick={preventDefault}>
      <SubHeading>{i18n.t(k.SORT_BY2)}</SubHeading>
      <ul>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(true)}>
          {i18n.t(k.ASCENDING)}
        </ListItem>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(false)}>
          {i18n.t(k.DESCENDING)}
        </ListItem>
      </ul>
      <Separator />
      <SubHeading>{i18n.t(k.FILTER)}</SubHeading>
      <BlanksNonBlanks handleSelect={handleSelect} filter={filter} />
      <Separator />
      {column.includes("-") && (
        <AllRecordsFilter
          label={`${i18n.t(k.ALL)} ${getAllRecordsLabel(
            allRecordsColumnId,
            fieldsById,
          )} ${i18n.t(k.RECORDS)}`}
          isAllRecords={allRecords[allRecordsColumnId]}
          columnId={allRecordsColumnId}
          workerData={workerData}
        />
      )}
      <ul>
        {values.map((value) => (
          <StyledChatroom
            tabIndex="0"
            role="button"
            key={value}
            onClick={(event) => handleSelect(event, value)}
          >
            <Checkbox
              id={`chatroom${value}`}
              checked={R.includes(value, filter || [])}
              handleChange={(event) => handleSelect(event, value)}
            />

            {value}
          </StyledChatroom>
        ))}
      </ul>
    </StyledFilter>
  );
};

export default Select;
